/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import "./App.scss";

//Used for Testing/Debugging Trimble Connection
import { useTrimbleConnect } from "./trimble/useTrimbleConnect";

//CSI Based Element Selection UI Panel
import { featureSelectionUiProvider } from "./features/FeatureSelectionUiProvider";

//Bentley Template UI Imports
import type { ScreenViewport } from "@itwin/core-frontend";
import { FitViewTool, IModelApp, StandardViewId } from "@itwin/core-frontend";
import { ECSchemaRpcInterface } from "@itwin/ecschema-rpcinterface-common";
import { Flex, ProgressLinear } from "@itwin/itwinui-react";
import {
  MeasurementActionToolbar,
  MeasureTools,
  MeasureToolsUiItemsProvider,
} from "@itwin/measure-tools-react";
import {
  AncestorsNavigationControls,
  CopyPropertyTextContextMenuItem,
  createPropertyGrid,
  PropertyGridManager,
  ShowHideNullValuesSettingsMenuItem,
} from "@itwin/property-grid-react";
import { StagePanelLocation, StagePanelSection } from "@itwin/appui-react";
import {
  CategoriesTreeComponent,
  createTreeWidget,
  ModelsTreeComponent,
  TreeWidget,
} from "@itwin/tree-widget-react";
import {
  useAccessToken,
  Viewer,
  ViewerContentToolsProvider,
  ViewerNavigationToolsProvider,
  ViewerPerformance,
  ViewerStatusbarItemsProvider,
} from "@itwin/web-viewer-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Auth } from "./Auth";
import { NiceConnectAspectWidget } from "./components/NiceConnectAspectWidget";
import { history } from "./history";
import { unifiedSelectionStorage } from "./selectionStorage";

const DEFAULT_ITWIN_ID = "3db4edae-02b0-44ed-864b-2308f6ad836a";
const DEFAULT_IMODEL_ID = "c811ec90-e52b-4da4-80ce-b91bbcc3b3cf";

const App: React.FC = () => {
  const [iModelId, setIModelId] = useState(DEFAULT_IMODEL_ID);
  const [iTwinId, setITwinId] = useState(DEFAULT_ITWIN_ID);
  const [changesetId, setChangesetId] = useState(
    process.env.IMJS_AUTH_CLIENT_CHANGESET_ID
  );

  // Bentley/iTwin token (unchanged)
  const accessToken = useAccessToken();

  // 🔹 Trimble Connect – for demo/display
  const {
    status: trimbleStatus,
    project: trimbleProject,
    error: trimbleError,
  } = useTrimbleConnect();

  useEffect(() => {
    console.log("Trimble status:", trimbleStatus);
    console.log("Trimble project:", trimbleProject);
    console.log("Trimble error:", trimbleError);
  }, [trimbleStatus, trimbleProject, trimbleError]);

  const authClient = Auth.getClient();

  const login = useCallback(async () => {
    try {
      await authClient.signInSilent();
    } catch {
      await authClient.signIn();
    }
  }, [authClient]);

  useEffect(() => {
    void login();
  }, [login]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("iTwinId")) {
      setITwinId(urlParams.get("iTwinId") as string);
    }
    if (urlParams.has("iModelId")) {
      setIModelId(urlParams.get("iModelId") as string);
    }
    if (urlParams.has("changesetId")) {
      setChangesetId(urlParams.get("changesetId") as string);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("iTwinId", iTwinId ?? "");

    if (iModelId) {
      searchParams.set("iModelId", iModelId);
    } else {
      searchParams.delete("iModelId");
    }

    if (changesetId) {
      searchParams.set("changesetId", changesetId);
    } else {
      searchParams.delete("changesetId");
    }

    history.push(`viewer?${searchParams.toString()}`);
  }, [iTwinId, iModelId, changesetId]);

  const viewConfiguration = useCallback((viewPort: ScreenViewport) => {
    const tileTreesLoaded = () => {
      return new Promise((resolve, reject) => {
        const start = new Date();
        const intvl = setInterval(() => {
          if (viewPort.areAllTileTreesLoaded) {
            ViewerPerformance.addMark("TilesLoaded");
            ViewerPerformance.addMeasure(
              "TileTreesLoaded",
              "ViewerStarting",
              "TilesLoaded"
            );
            clearInterval(intvl);
            resolve(true);
          }
          const now = new Date();
          if (now.getTime() - start.getTime() > 20000) {
            reject();
          }
        }, 100);
      });
    };

    tileTreesLoaded().finally(() => {
      void IModelApp.tools.run(FitViewTool.toolId, viewPort, true, false);
      viewPort.view.setStandardRotation(StandardViewId.Iso);
    });
  }, []);

  const viewCreatorOptions = useMemo(
    () => ({ viewportConfigurer: viewConfiguration }),
    [viewConfiguration]
  );

  const onIModelAppInit = useCallback(async () => {
    await TreeWidget.initialize();
    await PropertyGridManager.initialize();
    await MeasureTools.startup();
    MeasurementActionToolbar.setDefaultActionProvider();
  }, []);

  // ─────────────────────────────────────────
  //  RETURN – your original viewer + Trimble banner
  // ─────────────────────────────────────────
  return (
    <div className="viewer-container">
      {/*
      Trimble Connect banner for the demo
      <div
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 10,
          background: "rgba(0,0,0,0.7)",
          color: "white",
          padding: "4px 10px",
          borderRadius: 4,
          fontSize: 12,
          maxWidth: "40%",
          pointerEvents: "none",
        }}
      >
        {trimbleStatus === "ready" && trimbleProject && (
          <>
            <div style={{ opacity: 0.8 }}>Connected to Trimble project</div>
            <div style={{ fontWeight: 600 }}>{trimbleProject.name}</div>
            <div style={{ opacity: 0.8, fontSize: 11 }}>
              {trimbleProject.id}
            </div>
          </>
        )}
        {trimbleStatus === "connecting" && (
          <div>Connecting to Trimble Connect…</div>
        )}
        {trimbleStatus === "error" && (
          <div>Trimble integration error: {trimbleError}</div>
        )}
      </div>
      */}
      {!accessToken && (
        <Flex justifyContent="center" style={{ height: "100%" }}>
          <div className="signin-content">
            <ProgressLinear indeterminate={true} labels={["Signing in..."]} />
          </div>
        </Flex>
      )}

      <Viewer
        iTwinId={iTwinId ?? ""}
        iModelId={iModelId ?? ""}
        changeSetId={changesetId}
        authClient={authClient}
        viewCreatorOptions={viewCreatorOptions}
        enablePerformanceMonitors={true} // see description in the README
        onIModelAppInit={onIModelAppInit}
        mapLayerOptions={{
          BingMaps: {
            key: "key",
            value: process.env.IMJS_BING_MAPS_KEY ?? "",
          },
        }}
        backendConfiguration={{
          defaultBackend: {
            rpcInterfaces: [ECSchemaRpcInterface],
          },
        }}
        uiProviders={[
          new ViewerNavigationToolsProvider(),
          new ViewerContentToolsProvider({
            vertical: {
              measureGroup: false,
            },
          }),
          new ViewerStatusbarItemsProvider(),
          {
            id: "TreeWidgetUIProvider",
            getWidgets: () => [
              createTreeWidget({
                trees: [
                  {
                    id: ModelsTreeComponent.id,
                    getLabel: () => ModelsTreeComponent.getLabel(),
                    render: (props) => (
                      <ModelsTreeComponent
                        getSchemaContext={(iModel) => iModel.schemaContext}
                        density={props.density}
                        selectionStorage={unifiedSelectionStorage}
                        selectionMode={"extended"}
                        onPerformanceMeasured={props.onPerformanceMeasured}
                        onFeatureUsed={props.onFeatureUsed}
                      />
                    ),
                  },
                  {
                    id: CategoriesTreeComponent.id,
                    getLabel: () => CategoriesTreeComponent.getLabel(),
                    render: (props) => (
                      <CategoriesTreeComponent
                        getSchemaContext={(iModel) => iModel.schemaContext}
                        density={props.density}
                        selectionStorage={unifiedSelectionStorage}
                        onPerformanceMeasured={props.onPerformanceMeasured}
                        onFeatureUsed={props.onFeatureUsed}
                      />
                    ),
                  },
                ],
              }),
            ],
          },
          {
            id: "PropertyGridUIProvider",
            getWidgets: () => [
              createPropertyGrid({
                autoExpandChildCategories: true,
                ancestorsNavigationControls: (props) => (
                  <AncestorsNavigationControls {...props} />
                ),
                contextMenuItems: [
                  (props) => <CopyPropertyTextContextMenuItem {...props} />,
                ],
                settingsMenuItems: [
                  (props) => (
                    <ShowHideNullValuesSettingsMenuItem
                      {...props}
                      persist={true}
                    />
                  ),
                ],
              }),
              {
                id: "NiceConnectAspectWidget",
                label: "NiceConnect",
                content: <NiceConnectAspectWidget />,
                layouts: {
                  standard: {
                    location: StagePanelLocation.Right,
                    section: StagePanelSection.Start,
                  },
                },
              },
            ],
          },
          new MeasureToolsUiItemsProvider(),
          featureSelectionUiProvider,
        ]}
        selectionStorage={unifiedSelectionStorage}
      />
    </div>
  );
};

export default App;
